import React, { useEffect, useMemo, useRef, useState } from 'react';

const CONFIG = {
  baseFee: 49,
  includedChecks: 10,

  volume: {
    max: 25000,
  },

  pricingMode: 'threshold',

  tiers: [
    {
      id: 'entry',
      name: 'Entry',
      minVolume: 0,
      discount: 0,
    },
    {
      id: 't1',
      name: 'Growth',
      minVolume: 2000,
      discount: 0.24,
    },
    {
      id: 't2',
      name: 'Scale',
      minVolume: 10000,
      discount: 0.44,
    },
    {
      id: 't3',
      name: 'Wholesale',
      minVolume: 20000,
      discount: 0.6,
    },
  ],

  items: [
    {
      id: 'identity',
      name: 'Identity Verification',
      short: 'Identity',
      tierPrices: [5.0, 3.8, 2.8, 2.0],
      required: true,
      description: 'Confirms the person is who they say they are',
    },
    {
      id: 'pep',
      name: 'PEP Screening',
      short: 'PEP',
      tierPrices: [1.2, 0.91, 0.67, 0.48],
      description: 'Politically exposed persons',
    },
    {
      id: 'sanctions',
      name: 'Sanctions Screening',
      short: 'Sanactions',
      tierPrices: [0.9, 0.68, 0.5, 0.36],
      description: 'Global sanctions and watchlists',
    },
    {
      id: 'media',
      name: 'Adverse Media Screening',
      short: 'Media',
      tierPrices: [1.4, 1.06, 0.78, 0.56],
      description: 'Negative news across global media sources',
    },
    {
      id: 'watchlist',
      name: 'Wanted-list screening',
      short: 'Media',
      tierPrices: [0.8, 0.6, 0.44, 0.32],
      description: 'International law enforcement notices',
    },
  ],

  salesLabel: 'Talk to Sales',
  salesUrl: '#',
};

const nf = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormat = new Intl.NumberFormat('en-AU');

const getTierIndex = (volume) => {
  let index = 0;

  CONFIG.tiers.forEach((tier, i) => {
    if (volume >= tier.minVolume) {
      index = i;
    }
  });

  return index;
};

const calculatePricing = (volume, selected) => {
  const V = Number(volume) || 0;

  const selectedItems = CONFIG.items.filter((item) => selected.has(item.id));

  const tierIndex = getTierIndex(V);
  const tier = CONFIG.tiers[tierIndex];

  // Entry rate
  const b0 = selectedItems.reduce((sum, item) => sum + item.tierPrices[0], 0);

  // Current tier rate
  const listRate = selectedItems.reduce(
    (sum, item) => sum + item.tierPrices[tierIndex],
    0,
  );

  // First 10 checks are included in the base fee
  const overage = Math.max(0, V - CONFIG.includedChecks);

  let usage = 0;
  let blended = listRate;

  if (CONFIG.pricingMode === 'threshold') {
    usage = overage * listRate;
    blended = listRate;
  } else {
    let banded = 0;

    for (let k = 0; k <= tierIndex; k++) {
      const lower = CONFIG.tiers[k].minVolume;
      const upper = CONFIG.tiers[k + 1]?.minVolume ?? Infinity;

      if (V <= lower) continue;

      const quantity = Math.min(V, upper) - lower;

      const bandRate = selectedItems.reduce(
        (sum, item) => sum + item.tierPrices[k],
        0,
      );

      banded += quantity * bandRate;
    }

    usage = Math.max(0, banded - CONFIG.includedChecks * b0);

    blended = overage > 0 ? usage / overage : listRate;
  }

  const monthly = CONFIG.baseFee + usage;
  const annual = monthly * 12;

  const discount = b0 > 0 ? (b0 - blended) / b0 : 0;

  const savingPerCheck = Math.max(0, b0 - blended);

  const entryCost = CONFIG.baseFee + overage * b0;

  const savedYear = Math.max(0, (entryCost - monthly) * 12);

  const nextTier = CONFIG.tiers[tierIndex + 1];

  const addMore = nextTier ? Math.max(0, nextTier.minVolume - V) : 0;

  let nextTierRate = null;

  if (nextTier) {
    nextTierRate = selectedItems.reduce(
      (sum, item) => sum + item.tierPrices[tierIndex + 1],
      0,
    );
  }

  const nextTierSavingPerCheck =
    nextTierRate !== null ? Math.max(0, blended - nextTierRate) : 0;

  return {
    V,
    tierIndex,
    tier,
    b0,
    listRate,
    overage,
    usage,
    blended,
    monthly,
    annual,
    discount,
    savingPerCheck,
    entryCost,
    savedYear,
    nextTier,
    addMore,
    nextTierRate,
    nextTierSavingPerCheck,
    selectedItems,
  };
};

export default function WholesaleCalculator() {
  const [selected, setSelected] = useState(new Set(['identity']));

  // Empty initially as requested
  const [volume, setVolume] = useState('');

  // Inline tier-change message
  const [tierNotice, setTierNotice] = useState(null);

  // Used only to detect upward tier crossings.
  // This avoids showing a message on first render.
  const previousTierRef = useRef(null);

  const numericVolume = volume === '' ? 0 : Number(volume.replace(/,/g, ''));

  const isSales = numericVolume >= CONFIG.volume.max;

  const calc = useMemo(
    () => calculatePricing(numericVolume, selected),
    [numericVolume, selected],
  );

  /*
   * Detect only upward tier changes.
   *
   * Example:
   * 1999 -> 2000 = show Growth message
   * 2000 -> 5000 = no message
   * 5000 -> 1999 = no message
   */
  useEffect(() => {
    const currentTierIndex = calc.tierIndex;

    // Don't show anything on initial render.
    if (previousTierRef.current === null) {
      previousTierRef.current = currentTierIndex;
      return;
    }

    // Only show when moving UP to a new tier.
    if (currentTierIndex > previousTierRef.current) {
      const tier = CONFIG.tiers[currentTierIndex];

      setTierNotice({
        tier,
        discount: calc.discount,
        rate: calc.blended,
        savingPerCheck: calc.savingPerCheck,
      });

      // Hide automatically after 4 seconds.
      const timer = setTimeout(() => {
        setTierNotice(null);
      }, 4000);

      previousTierRef.current = currentTierIndex;

      return () => clearTimeout(timer);
    }

    previousTierRef.current = currentTierIndex;
  }, [calc.tierIndex, calc.discount, calc.blended, calc.savingPerCheck]);
  const handleVolumeInput = (e) => {
    const raw = e.target.value;

    // Only digits
    const digits = raw.replace(/\D/g, '');

    setVolume(digits);
  };

  const handleVolumeBlur = () => {
    if (volume === '') return;

    const value = Number(volume);

    if (!Number.isFinite(value)) {
      setVolume('');
      return;
    }

    setVolume(String(Math.max(0, value)));
  };

  const handleSlider = (e) => {
    setVolume(String(Number(e.target.value)));
  };

  const toggleItem = (item) => {
    if (item.required) return;

    const next = new Set(selected);

    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }

    setSelected(next);
  };

  const sliderValue = Math.min(CONFIG.volume.max, numericVolume);

  const sliderPercentage = (sliderValue / CONFIG.volume.max) * 100;

  const currentTierIcon =
    {
      entry: 'rocket_launch',
      t1: 'trending_up',
      t2: 'stacked_bar_chart',
      t3: 'business',
    }[calc.tier.id] || 'verified';

  return (
    <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 p-6 shadow-2xl md:p-10">
      {/* Background decoration */}
      <div className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-blue-100/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-indigo-100/30 blur-3xl" />

      <div className="relative">
        {/* HEADER */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-blue-50">
              <span className="material-symbols-outlined text-[20px] text-[#375AE7]">
                calculate
              </span>
            </div>

            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                Calculate Your Estimated Monthly Price
              </h1>

              <p className="mt-0.5 text-xs text-slate-500">
                Self-Serve pricing calculator
              </p>
            </div>
          </div>

          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-600 md:text-sm">
            Select the verification services you need and enter your expected
            monthly volume. Your rate and estimated spend will update
            automatically.
          </p>
        </div>
        {tierNotice && !isSales && (
          <div
            className="
      mt-4
      overflow-hidden
      rounded-2xl
      border border-emerald-200
      bg-emerald-50
      shadow-sm mb-4
      animate-[fadeIn_300ms_ease-out]
    "
          >
            <div className="flex items-start gap-3 p-4">
              {/* Discount icon */}
              <div
                className="
          flex h-9 w-9 shrink-0
          items-center justify-center
          rounded-xl
          bg-emerald-100
          border border-emerald-200
        "
              >
                <span className="material-symbols-outlined text-[18px] text-emerald-600">
                  local_offer
                </span>
              </div>

              <div className="min-w-0 flex-1">
                {/* Main message */}
                <div className="flex items-center gap-2">
                  <p className="text-xs font-bold text-emerald-800">
                    You’ve reached the {tierNotice.tier.name} tier
                  </p>

                  {/* <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold text-emerald-700">
                        {Math.round(tierNotice.discount * 100)}% discount
                      </span> */}
                </div>

                {/* Supporting information */}
                <p className="mt-1 text-[11px] leading-relaxed text-emerald-700">
                  Your monthly volume has unlocked a{' '}
                  <strong>
                    {Math.round(tierNotice.discount * 100)}% discount
                  </strong>{' '}
                  on your verification pricing.
                </p>

                {/* Pricing details */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <div className="rounded-lg border border-emerald-100 bg-white px-2.5 py-1.5">
                    <span className="text-[10px] text-slate-400">Rate</span>
                    <span className="ml-1 text-[10px] font-bold text-slate-700">
                      {nf.format(tierNotice.rate)}/check
                    </span>
                  </div>

                  <div className="rounded-lg border border-emerald-100 bg-white px-2.5 py-1.5">
                    <span className="text-[10px] text-slate-400">You save</span>
                    <span className="ml-1 text-[10px] font-bold text-emerald-700">
                      {nf.format(tierNotice.savingPerCheck)}/check
                    </span>
                  </div>
                </div>
              </div>

              {/* Close */}
              <button
                type="button"
                onClick={() => setTierNotice(null)}
                className="
          flex h-6 w-6 shrink-0
          items-center justify-center
          rounded-lg
          text-emerald-500
          transition
          hover:bg-emerald-100
          hover:text-emerald-700
        "
                aria-label="Dismiss"
              >
                <span className="material-symbols-outlined text-[16px]">
                  close
                </span>
              </button>
            </div>

            {/* Temporary progress indicator */}
            <div className="h-0.5 w-full bg-emerald-100">
              <div
                className="
          h-full
          origin-left
          bg-emerald-500
          animate-[shrink_4500ms_linear_forwards]
        "
              />
            </div>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-2">
          {/* =====================================================
              LEFT PANEL
          ====================================================== */}
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur-xl">
            {/* Features */}
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50">
                <span className="material-symbols-outlined text-[#375AE7]">
                  verified_user
                </span>
              </div>

              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Verification Features
                </h3>

                <p className="text-xs text-slate-500">
                  Select the checks you need
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {CONFIG.items.map((item) => {
                const active = selected.has(item.id);
                const currentPrice = item.tierPrices[calc.tierIndex];

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggleItem(item)}
                    className={`
          group flex w-full items-center justify-between
          rounded-2xl border p-4
          text-left transition-all duration-300
          ${
            active
              ? 'border-[#375AE7] bg-blue-50 shadow-md shadow-blue-100'
              : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
          }
        `}
                  >
                    {/* Left */}
                    <div className="flex min-w-0 items-start gap-4">
                      {/* Checkbox */}
                      <div
                        className={`
              mt-0.5 flex h-6 w-6 shrink-0
              items-center justify-center rounded-full border
              transition-all
              ${
                active
                  ? 'border-[#375AE7] bg-[#375AE7]'
                  : 'border-slate-300 bg-white'
              }
            `}
                      >
                        {active && (
                          <span className="material-symbols-outlined text-[15px] text-white">
                            check
                          </span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {item.name}
                          </p>

                          {item.required && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#375AE7]">
                              Required
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        <p className="mt-1 max-w-xl text-[11px] leading-relaxed text-slate-500">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="ml-4 shrink-0 text-right">
                      <p className="text-sm font-bold text-slate-800">
                        {nf.format(currentPrice)}
                      </p>

                      <p className="mt-1 text-[10px] text-slate-400">
                        / verification
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* =====================================================
                VOLUME
            ====================================================== */}
            <div className="mt-8 rounded-3xl border border-blue-100 bg-gradient-to-br from-white via-blue-50/40 to-indigo-50/60 p-5 shadow-sm">
              {/* Volume heading */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-100 to-indigo-100">
                    <span className="material-symbols-outlined text-[#375AE7]">
                      stacked_bar_chart
                    </span>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      Monthly volume
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500">
                      How many verifications do you expect each month?
                    </p>
                  </div>
                </div>

                {/* Current volume */}
                <div className="rounded-xl border border-blue-100 bg-white px-3 py-2 text-right shadow-sm">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                    Volume
                  </p>

                  <p className="mt-0.5 text-sm font-bold tabular-nums text-[#375AE7]">
                    {numberFormat.format(numericVolume)}
                  </p>
                </div>
              </div>

              {/* Slider */}
              <div className="mt-7">
                <input
                  type="range"
                  min="0"
                  max={CONFIG.volume.max}
                  step="10"
                  value={sliderValue}
                  onChange={handleSlider}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full outline-none"
                  style={{
                    background: `linear-gradient(
                      to right,
                      #375AE7 0%,
                      #6366F1 ${sliderPercentage}%,
                      #E2E8F0 ${sliderPercentage}%,
                      #E2E8F0 100%
                    )`,
                  }}
                />

                <div className="mt-2 flex justify-between text-[10px] font-medium text-slate-400">
                  <span>0</span>
                  <span>2K</span>
                  <span>10K</span>
                  <span>20K</span>
                  <span>25K</span>
                </div>
              </div>

              {/* Manual input */}
              <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                      <span className="material-symbols-outlined text-[17px] text-[#375AE7]">
                        edit
                      </span>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        Enter volume
                      </p>

                      <p className="text-[10px] text-slate-400">
                        Verifications per month
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <input
                      id="rtf-vol-input"
                      type="text"
                      inputMode="numeric"
                      value={
                        volume === '' ? '' : numberFormat.format(numericVolume)
                      }
                      onChange={handleVolumeInput}
                      onBlur={handleVolumeBlur}
                      placeholder="Enter volume"
                      className="w-28 bg-transparent text-right text-sm font-bold text-slate-900 outline-none placeholder:text-slate-300"
                    />
                  </div>
                </div>
              </div>

              {/* Sales ceiling */}
              {isSales && (
                <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                    <span className="material-symbols-outlined text-[18px] text-amber-600">
                      handshake
                    </span>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-amber-800">
                      Wholesale volume reached
                    </p>

                    <p className="mt-1 text-[11px] leading-relaxed text-amber-700">
                      Volumes of {numberFormat.format(CONFIG.volume.max)}+
                      require a tailored pricing package. Your entered volume
                      will be passed to our sales team.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* =====================================================
              RIGHT PANEL
          ====================================================== */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
            {/* Current tier heading */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50">
                  <span className="material-symbols-outlined text-[#375AE7]">
                    receipt_long
                  </span>
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                    Your pricing
                  </p>

                  <h2 className="mt-0.5 text-base font-semibold text-slate-900">
                    Based on your monthly volume
                  </h2>
                </div>
              </div>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-semibold text-slate-500">
                AUD
              </span>
            </div>

            {/* =================================================
                TIER HERO
            ================================================== */}
            <div
              className={`
                mt-6 overflow-hidden rounded-3xl border p-6
                ${
                  isSales
                    ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50'
                    : 'border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50'
                }
              `}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-white px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#375AE7] shadow-sm">
                      Current tier
                    </span>
                  </div>

                  <h3 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
                    {isSales ? 'Wholesale' : calc.tier.name}
                  </h3>

                  {!isSales && (
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-[#375AE7]">
                        {nf.format(calc.blended)}
                      </span>

                      <span className="text-xs text-slate-500">
                        per verification
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
                  <span className="material-symbols-outlined text-[24px] text-[#375AE7]">
                    {isSales ? 'handshake' : currentTierIcon}
                  </span>
                </div>
              </div>

              {!isSales && (
                <>
                  {/* Discount statement */}
                  <div className="mt-5 flex items-center gap-3 rounded-2xl border border-white bg-white/80 p-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                      <span className="material-symbols-outlined text-[18px] text-emerald-600">
                        local_offer
                      </span>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-emerald-700">
                        {Math.round(calc.discount * 100)}% discount
                      </p>

                      <p className="mt-0.5 text-[11px] text-slate-500">
                        Saving {nf.format(calc.savingPerCheck)} per verification
                      </p>
                    </div>
                  </div>

                  {/* Tier progress */}
                  <div className="mt-5">
                    <div className="flex justify-between text-[10px] font-medium text-slate-400">
                      <span>{calc.tier.minVolume.toLocaleString()} checks</span>

                      <span>
                        {calc.nextTier
                          ? `${calc.nextTier.minVolume.toLocaleString()} next tier`
                          : 'Maximum published tier'}
                      </span>
                    </div>

                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
                      <div
                        className="h-full rounded-full bg-[#375AE7] transition-all duration-500"
                        style={{
                          width: `${
                            calc.nextTier
                              ? Math.min(
                                  100,
                                  ((numericVolume - calc.tier.minVolume) /
                                    (calc.nextTier.minVolume -
                                      calc.tier.minVolume)) *
                                    100,
                                )
                              : 100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* =================================================
                TIER CHANGE MESSAGE
            ================================================== */}

            {/* =================================================
                CALCULATION BREAKDOWN
            ================================================== */}
            {!isSales && (
              <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Pricing breakdown
                    </h3>

                    <p className="mt-1 text-[11px] text-slate-400">
                      How your monthly estimate is calculated
                    </p>
                  </div>

                  <span className="rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                    {numberFormat.format(numericVolume)} checks
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Selected items */}
                  {calc.selectedItems.map((item) => {
                    const price = item.tierPrices[calc.tierIndex];

                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                            <span className="material-symbols-outlined text-[15px] text-[#375AE7]">
                              verified
                            </span>
                          </div>

                          <span className="text-xs font-medium text-slate-700">
                            {item.name}
                          </span>
                        </div>

                        <span className="text-sm font-semibold text-slate-900">
                          {nf.format(price)}
                        </span>
                      </div>
                    );
                  })}

                  <div className="border-t border-dashed border-slate-200" />

                  {/* Base fee */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        Base fee
                      </p>

                      <p className="mt-0.5 text-[10px] text-slate-400">
                        Includes first {CONFIG.includedChecks} verifications
                      </p>
                    </div>

                    <span className="text-sm font-semibold text-slate-900">
                      {nf.format(CONFIG.baseFee)}
                    </span>
                  </div>

                  {/* Overage */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        Additional verifications
                      </p>

                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {numberFormat.format(calc.overage)} ×{' '}
                        {nf.format(calc.blended)}
                      </p>
                    </div>

                    <span className="text-sm font-semibold text-slate-900">
                      {nf.format(calc.usage)}
                    </span>
                  </div>

                  <div className="border-t border-slate-200" />

                  {/* Monthly */}
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        Est. monthly spend
                      </p>

                      <p className="mt-1 text-[10px] text-slate-400">
                        Base fee + usage
                      </p>
                    </div>

                    <span className="text-2xl font-bold text-[#375AE7]">
                      {nf.format(calc.monthly)}
                    </span>
                  </div>

                  {/* Annual */}
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[17px] text-[#375AE7]">
                          calendar_month
                        </span>

                        <span className="text-xs font-medium text-slate-600">
                          Estimated annual spend
                        </span>
                      </div>

                      <span className="text-sm font-bold text-slate-900">
                        {nf.format(calc.annual)}
                      </span>
                    </div>

                    <p className="mt-2 text-[10px] text-slate-400">
                      Monthly estimate × 12
                    </p>
                  </div>

                  {/* Savings */}
                  {calc.savedYear > 0 && (
                    <div className="flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[17px] text-emerald-600">
                          local_offer
                        </span>

                        <div>
                          <p className="text-xs font-semibold text-emerald-700">
                            Estimated annual savings
                          </p>

                          <p className="mt-0.5 text-[10px] text-emerald-600">
                            Compared with standard pricing
                          </p>
                        </div>
                      </div>

                      <span className="text-sm font-bold text-emerald-700">
                        {nf.format(calc.savedYear)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* =================================================
                NEXT TIER
            ================================================== */}
            {!isSales && calc.nextTier && (
              <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100">
                    <span className="material-symbols-outlined text-[18px] text-[#375AE7]">
                      trending_up
                    </span>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold text-slate-800">
                        Next tier: {calc.nextTier.name}
                      </p>

                      <span className="text-[10px] font-bold text-[#375AE7]">
                        {calc.nextTier.minVolume.toLocaleString()} checks
                      </span>
                    </div>

                    <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                      Add{' '}
                      <strong className="text-slate-700">
                        {numberFormat.format(calc.addMore)}
                      </strong>{' '}
                      more verifications to unlock the{' '}
                      <strong className="text-slate-700">
                        {Math.round(calc.nextTier.discount * 100)}%
                      </strong>{' '}
                      tier discount.
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-[10px] font-semibold text-slate-600">
                        Current: {nf.format(calc.blended)}/check
                      </span>

                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-[10px] font-semibold text-[#375AE7]">
                        Next: {nf.format(calc.nextTierRate)}/check
                      </span>

                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-[10px] font-semibold text-emerald-700">
                        Save {nf.format(calc.nextTierSavingPerCheck)}/check
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                WHOLESALE / SALES
            ================================================== */}
            {isSales && (
              <div className="mt-5 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100">
                    <span className="material-symbols-outlined text-[24px] text-amber-600">
                      handshake
                    </span>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-600">
                      Custom pricing
                    </p>

                    <h3 className="mt-1 text-xl font-bold text-slate-900">
                      Let’s build your wholesale package
                    </h3>

                    <p className="mt-2 text-xs leading-relaxed text-slate-600">
                      Your volume of{' '}
                      <strong>{numberFormat.format(numericVolume)}</strong> is
                      above the published self-serve pricing limit. Our sales
                      team can provide a tailored package for your expected
                      verification volume.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* =================================================
                CTA
            ================================================== */}
            <div className="mt-5">
              {/* <button
                type="button"
                onClick={() => {
                  if (isSales) {
                    window.location.href = CONFIG.salesUrl;
                  }
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#375AE7] py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-100 transition-all duration-200 hover:bg-blue-700 hover:shadow-xl"
              >
                <span className="material-symbols-outlined text-[18px]">
                  {isSales ? 'support_agent' : 'workspace_premium'}
                </span>

                {isSales ? CONFIG.salesLabel : 'Subscribe now'}
              </button> */}

              <button
                type="button"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 py-3.5 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50"
              >
                <span className="material-symbols-outlined text-[18px]">
                  support_agent
                </span>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
